import { HeroCarousel } from "@/components/hero-carousel"
import { CategoryGrid } from "@/components/category-grid"
import { ProductGrid } from "@/components/product-grid"
import { useLanguage } from "@/lib/language-context"
import { useScrollAnimation, useCountUp } from "@/hooks/use-scroll-animation"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Leaf,
  Shield,
  Truck,
  Star,
  Users,
  Award,
  Phone,
  ArrowRight,
  CheckCircle,
  Quote,
  ChevronRight,
  Heart,
  Sparkles,
  Headphones,
  Package,
  Clock,
  Mail,
  MessageCircle,
  Zap,
  ThumbsUp,
  Globe,
} from "lucide-react"
import { Link } from "react-router-dom"
import { cn } from "@/lib/utils"
import type { HeroSlide, Category, Product } from "@/lib/api"

interface HomeContentProps {
  heroSlides: HeroSlide[]
  categories: Category[]
  featuredProducts: Product[]
}

export function HomeContent({ heroSlides, categories, featuredProducts }: HomeContentProps) {
  const { t } = useLanguage()

  // Scroll animations
  const trustBar = useScrollAnimation()
  const categoriesSection = useScrollAnimation()
  const aboutSection = useScrollAnimation()
  const productsSection = useScrollAnimation()
  const benefitsSection = useScrollAnimation()
  const statsSection = useScrollAnimation()
  const testimonialsSection = useScrollAnimation()
  const processSection = useScrollAnimation()
  const faqSection = useScrollAnimation()
  const brandsSection = useScrollAnimation()
  const newsletterSection = useScrollAnimation()
  const ctaSection = useScrollAnimation()

  // Count up animations
  const customersCount = useCountUp(50000, 2500, statsSection.isVisible)
  const productsCount = useCountUp(500, 2500, statsSection.isVisible)
  const experienceCount = useCountUp(15, 2500, statsSection.isVisible)
  const reviewsCount = useCountUp(99, 2500, statsSection.isVisible)

  return (
    <div className="flex flex-col overflow-hidden">
      {/* Hero Section */}
      <HeroCarousel slides={heroSlides} />

      {/* Trust Bar */}
      <section
        ref={trustBar.ref}
        className={cn(
          "bg-primary text-primary-foreground py-6 transition-all duration-700",
          trustBar.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
        )}
      >
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center lg:justify-between items-center gap-6 lg:gap-8">
            {[
              { icon: Truck, text: t("home", "trust_shipping") },
              { icon: Shield, text: t("home", "trust_quality") },
              { icon: Headphones, text: t("home", "trust_support") },
              { icon: Leaf, text: t("home", "trust_natural") },
            ].map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-3 group cursor-default"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="p-2.5 bg-primary-foreground/10 rounded-xl group-hover:bg-primary-foreground/20 group-hover:scale-110 transition-all">
                  <item.icon className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section ref={categoriesSection.ref} className="py-20 lg:py-28 bg-background" id="categories">
        <div className="container mx-auto px-4">
          <div
            className={cn(
              "flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 transition-all duration-700",
              categoriesSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
            )}
          >
            <div>
              <span className="inline-flex items-center gap-2 text-primary font-semibold mb-4 text-sm tracking-wide uppercase">
                <Sparkles className="w-4 h-4 animate-pulse" />
                {t("home", "categories_badge")}
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-balance leading-tight">
                {t("home", "categories_title")}
              </h2>
              <p className="text-muted-foreground mt-3 text-lg max-w-xl">{t("home", "categories_subtitle")}</p>
            </div>
            <Link to="/categories">
              <Button
                variant="outline"
                className="gap-2 bg-transparent group hover:bg-primary hover:text-primary-foreground px-6 py-5"
              >
                {t("home", "categories_view_all")}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
          <div
            className={cn(
              "transition-all duration-700 delay-200",
              categoriesSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
            )}
          >
            <CategoryGrid categories={categories} limit={9} />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section ref={aboutSection.ref} className="py-20 lg:py-28 bg-muted/40 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div
              className={cn(
                "relative transition-all duration-1000",
                aboutSection.isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-16",
              )}
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl img-zoom">
                <img
                  src="/natural-herbal-medicine-laboratory-organic-product.jpg"
                  alt={t("home", "about_badge")}
                  className="w-full h-auto aspect-[4/3] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/30 via-transparent to-transparent" />
              </div>

              {/* Floating experience card */}
              <div className="absolute -bottom-6 -right-6 bg-card p-6 rounded-2xl shadow-2xl border border-border/50 hidden md:flex items-center gap-4 hover-lift card-shine">
                <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/5 rounded-2xl flex items-center justify-center">
                  <Award className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <p className="text-4xl font-bold gradient-text">15+</p>
                  <p className="text-muted-foreground text-sm">{t("home", "about_experience")}</p>
                </div>
              </div>

              {/* Floating quality badge */}
              <div className="absolute -top-4 -left-4 bg-primary text-primary-foreground p-4 rounded-2xl shadow-xl hidden md:flex items-center gap-3 animate-float">
                <Shield className="w-6 h-6" />
                <span className="font-semibold text-sm">100% Sifat</span>
              </div>

              {/* Decorative blurs */}
              <div className="absolute -top-8 -left-8 w-32 h-32 bg-primary/15 rounded-full blur-3xl" />
              <div className="absolute -bottom-12 right-1/4 w-40 h-40 bg-accent/25 rounded-full blur-3xl" />
            </div>

            <div
              className={cn(
                "transition-all duration-1000 delay-200",
                aboutSection.isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-16",
              )}
            >
              <span className="inline-flex items-center gap-2 text-primary font-semibold mb-4 text-sm tracking-wide uppercase">
                <Heart className="w-4 h-4" />
                {t("home", "about_badge")}
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-balance leading-[1.15]">
                {t("home", "about_title")}
              </h2>
              <p className="text-muted-foreground text-lg mb-8 text-pretty leading-relaxed">
                {t("home", "about_text")}
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-10">
                {[
                  { text: t("home", "about_feature_1"), icon: Leaf },
                  { text: t("home", "about_feature_2"), icon: Shield },
                  { text: t("home", "about_feature_3"), icon: ThumbsUp },
                  { text: t("home", "about_feature_4"), icon: Globe },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-4 rounded-xl bg-background hover:bg-primary/5 transition-all group hover-lift"
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <item.icon className="w-5 h-5 text-primary" />
                    </div>
                    <span className="font-medium">{item.text}</span>
                  </div>
                ))}
              </div>

              <Button
                size="lg"
                className="gap-2 group shadow-xl shadow-primary/20 hover:shadow-2xl hover:shadow-primary/30 btn-glow"
              >
                {t("home", "about_cta")}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section ref={productsSection.ref} className="py-20 lg:py-28 bg-background" id="products">
        <div className="container mx-auto px-4">
          <div
            className={cn(
              "flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 transition-all duration-700",
              productsSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
            )}
          >
            <div>
              <span className="inline-flex items-center gap-2 text-primary font-semibold mb-4 text-sm tracking-wide uppercase">
                <Star className="w-4 h-4 fill-primary" />
                {t("home", "products_badge")}
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-balance">{t("home", "products_title")}</h2>
              <p className="text-muted-foreground mt-3 text-lg max-w-xl">{t("home", "products_subtitle")}</p>
            </div>
            <Link to="/products">
              <Button
                variant="outline"
                className="gap-2 bg-transparent group hover:bg-primary hover:text-primary-foreground px-6 py-5"
              >
                {t("home", "products_view_all")}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
          <div
            className={cn(
              "transition-all duration-700 delay-200",
              productsSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
            )}
          >
            <ProductGrid products={featuredProducts} limit={12} />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section
        ref={benefitsSection.ref}
        className="py-20 lg:py-28 bg-gradient-to-b from-primary/5 via-muted/30 to-background"
      >
        <div className="container mx-auto px-4">
          <div
            className={cn(
              "text-center mb-16 transition-all duration-700",
              benefitsSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
            )}
          >
            <span className="inline-flex items-center gap-2 text-primary font-semibold mb-4 text-sm tracking-wide uppercase">
              <Zap className="w-4 h-4" />
              {t("home", "benefits_badge")}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">{t("home", "benefits_title")}</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">{t("home", "benefits_subtitle")}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Leaf,
                title: t("home", "benefit_1_title"),
                desc: t("home", "benefit_1_desc"),
                gradient: "from-green-500/20 to-emerald-500/5",
              },
              {
                icon: Shield,
                title: t("home", "benefit_2_title"),
                desc: t("home", "benefit_2_desc"),
                gradient: "from-blue-500/20 to-cyan-500/5",
              },
              {
                icon: Truck,
                title: t("home", "benefit_3_title"),
                desc: t("home", "benefit_3_desc"),
                gradient: "from-amber-500/20 to-orange-500/5",
              },
              {
                icon: Users,
                title: t("home", "benefit_4_title"),
                desc: t("home", "benefit_4_desc"),
                gradient: "from-purple-500/20 to-pink-500/5",
              },
            ].map((item, index) => (
              <div
                key={index}
                className={cn(
                  "bg-card p-8 rounded-3xl border border-border/50 hover-lift card-shine group cursor-default transition-all duration-500",
                  benefitsSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
                )}
                style={{ transitionDelay: `${index * 100 + 200}ms` }}
              >
                <div
                  className={cn(
                    "w-18 h-18 rounded-2xl flex items-center justify-center mb-6 bg-gradient-to-br transition-all duration-500",
                    item.gradient,
                    "group-hover:scale-110 group-hover:rotate-3",
                  )}
                >
                  <item.icon className="w-9 h-9 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors">{item.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section
        ref={statsSection.ref}
        className="py-24 lg:py-32 bg-primary text-primary-foreground relative overflow-hidden"
      >
        {/* Decorative background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-float-slow" />
          <div
            className="absolute bottom-0 right-1/4 w-80 h-80 bg-white/5 rounded-full blur-3xl animate-float-slow"
            style={{ animationDelay: "3s" }}
          />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-white/5 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/5 rounded-full" />
        </div>

        <div className="container mx-auto px-4 relative">
          <div
            className={cn(
              "text-center mb-16 transition-all duration-700",
              statsSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
            )}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">{t("home", "stats_title")}</h2>
            <p className="text-primary-foreground/80 text-lg max-w-xl mx-auto">{t("home", "stats_subtitle")}</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {[
              { value: customersCount, suffix: "+", label: t("home", "stat_customers"), icon: Users },
              { value: productsCount, suffix: "+", label: t("home", "stat_products"), icon: Package },
              { value: experienceCount, suffix: "+", label: t("home", "stat_experience"), icon: Award },
              { value: reviewsCount, suffix: "%", label: t("home", "stat_reviews"), icon: ThumbsUp },
            ].map((stat, index) => (
              <div
                key={index}
                className={cn(
                  "text-center p-6 rounded-2xl bg-white/5 backdrop-blur-sm hover:bg-white/10 transition-all duration-500 group",
                  statsSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
                )}
                style={{ transitionDelay: `${index * 100 + 200}ms` }}
              >
                <stat.icon className="w-8 h-8 mx-auto mb-4 opacity-70 group-hover:scale-110 transition-transform" />
                <p className="text-4xl md:text-5xl lg:text-6xl font-bold mb-2">
                  {stat.value.toLocaleString()}
                  {stat.suffix}
                </p>
                <p className="text-primary-foreground/80 text-lg">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works / Process Section */}
      <section ref={processSection.ref} className="py-20 lg:py-28 bg-background">
        <div className="container mx-auto px-4">
          <div
            className={cn(
              "text-center mb-16 transition-all duration-700",
              processSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
            )}
          >
            <span className="inline-flex items-center gap-2 text-primary font-semibold mb-4 text-sm tracking-wide uppercase">
              <Clock className="w-4 h-4" />
              {t("home", "process_badge")}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">{t("home", "process_title")}</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">{t("home", "process_subtitle")}</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 relative">
            {/* Connection line */}
            <div className="hidden md:block absolute top-16 left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-primary/20 via-primary to-primary/20" />

            {[
              { step: "01", title: t("home", "process_1_title"), desc: t("home", "process_1_desc"), icon: Sparkles },
              { step: "02", title: t("home", "process_2_title"), desc: t("home", "process_2_desc"), icon: Package },
              { step: "03", title: t("home", "process_3_title"), desc: t("home", "process_3_desc"), icon: Truck },
              { step: "04", title: t("home", "process_4_title"), desc: t("home", "process_4_desc"), icon: Heart },
            ].map((item, index) => (
              <div
                key={index}
                className={cn(
                  "text-center relative transition-all duration-700",
                  processSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
                )}
                style={{ transitionDelay: `${index * 150 + 200}ms` }}
              >
                <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-primary/10 to-primary/5 rounded-3xl flex items-center justify-center relative group hover-lift">
                  <item.icon className="w-12 h-12 text-primary group-hover:scale-110 transition-transform" />
                  <span className="absolute -top-2 -right-2 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                    {item.step}
                  </span>
                </div>
                <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section ref={testimonialsSection.ref} className="py-20 lg:py-28 bg-muted/40">
        <div className="container mx-auto px-4">
          <div
            className={cn(
              "text-center mb-16 transition-all duration-700",
              testimonialsSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
            )}
          >
            <span className="inline-flex items-center gap-2 text-primary font-semibold mb-4 text-sm tracking-wide uppercase">
              <MessageCircle className="w-4 h-4" />
              {t("home", "testimonials_badge")}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">{t("home", "testimonials_title")}</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                text: t("home", "testimonial_1_text"),
                name: t("home", "testimonial_1_name"),
                role: t("home", "testimonial_1_role"),
                avatar: "A",
                color: "from-green-500 to-emerald-600",
              },
              {
                text: t("home", "testimonial_2_text"),
                name: t("home", "testimonial_2_name"),
                role: t("home", "testimonial_2_role"),
                avatar: "B",
                color: "from-blue-500 to-cyan-600",
              },
              {
                text: t("home", "testimonial_3_text"),
                name: t("home", "testimonial_3_name"),
                role: t("home", "testimonial_3_role"),
                avatar: "M",
                color: "from-purple-500 to-pink-600",
              },
            ].map((testimonial, index) => (
              <div
                key={index}
                className={cn(
                  "bg-card p-8 rounded-3xl border border-border/50 hover-lift card-shine transition-all duration-700",
                  testimonialsSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
                )}
                style={{ transitionDelay: `${index * 150 + 200}ms` }}
              >
                <Quote className="w-10 h-10 text-primary/20 mb-4" />
                <div className="flex items-center gap-1 mb-6">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-8 text-pretty text-lg leading-relaxed italic">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-4">
                  <div
                    className={cn(
                      "w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold bg-gradient-to-br",
                      testimonial.color,
                    )}
                  >
                    {testimonial.avatar}
                  </div>
                  <div>
                    <p className="font-bold text-lg">{testimonial.name}</p>
                    <p className="text-muted-foreground">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section ref={faqSection.ref} className="py-20 lg:py-28 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div
              className={cn(
                "text-center mb-12 transition-all duration-700",
                faqSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
              )}
            >
              <span className="inline-flex items-center gap-2 text-primary font-semibold mb-4 text-sm tracking-wide uppercase">
                <CheckCircle className="w-4 h-4" />
                {t("home", "faq_badge")}
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">{t("home", "faq_title")}</h2>
            </div>

            <div className="space-y-4">
              {[
                { q: t("home", "faq_1_q"), a: t("home", "faq_1_a") },
                { q: t("home", "faq_2_q"), a: t("home", "faq_2_a") },
                { q: t("home", "faq_3_q"), a: t("home", "faq_3_a") },
                { q: t("home", "faq_4_q"), a: t("home", "faq_4_a") },
              ].map((faq, index) => (
                <details
                  key={index}
                  className={cn(
                    "group bg-card rounded-2xl border border-border/50 overflow-hidden transition-all duration-700 hover:shadow-lg",
                    faqSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
                  )}
                  style={{ transitionDelay: `${index * 100 + 200}ms` }}
                >
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none font-bold text-lg hover:bg-muted/50 transition-colors">
                    {faq.q}
                    <ChevronRight className="w-5 h-5 text-primary transition-transform duration-300 group-open:rotate-90" />
                  </summary>
                  <div className="px-6 pb-6 text-muted-foreground leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section
        ref={newsletterSection.ref}
        className="py-20 lg:py-28 bg-gradient-to-br from-primary/10 via-muted/50 to-accent/10"
      >
        <div className="container mx-auto px-4">
          <div
            className={cn(
              "max-w-2xl mx-auto text-center transition-all duration-700",
              newsletterSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
            )}
          >
            <span className="inline-flex items-center gap-2 text-primary font-semibold mb-4 text-sm tracking-wide uppercase">
              <Mail className="w-4 h-4" />
              {t("home", "newsletter_badge")}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">{t("home", "newsletter_title")}</h2>
            <p className="text-muted-foreground text-lg mb-8">{t("home", "newsletter_text")}</p>

            <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder={t("home", "newsletter_placeholder")}
                className="flex-1 h-14 px-6 text-lg rounded-xl border-2 focus:border-primary"
              />
              <Button
                size="lg"
                className="h-14 px-8 rounded-xl shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 btn-glow"
              >
                {t("home", "newsletter_button")}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        ref={ctaSection.ref}
        className="py-20 lg:py-28 bg-primary text-primary-foreground relative overflow-hidden"
      >
        {/* Background decorations */}
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
          <Leaf className="absolute top-20 left-1/4 w-16 h-16 text-white/10 animate-float" />
          <Leaf
            className="absolute bottom-20 right-1/4 w-12 h-12 text-white/10 animate-float"
            style={{ animationDelay: "1s" }}
          />
        </div>

        <div className="container mx-auto px-4 relative">
          <div
            className={cn(
              "text-center max-w-3xl mx-auto transition-all duration-700",
              ctaSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
            )}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">{t("home", "cta_title")}</h2>
            <p className="text-xl text-primary-foreground/80 mb-10">{t("home", "cta_text")}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/products">
                <Button
                  size="lg"
                  variant="secondary"
                  className="text-lg px-10 py-6 bg-background text-foreground hover:bg-background/90 shadow-xl group"
                >
                  {t("home", "cta_button")}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-10 py-6 border-2 border-primary-foreground/30 bg-transparent hover:bg-primary-foreground/10 text-primary-foreground"
              >
                <Phone className="mr-2 w-5 h-5" />
                {t("home", "cta_call")}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
